import { useState, useLayoutEffect } from 'react';
import { isViewportZoomedFromBaseline, subscribeViewportZoomChanges } from './viewportZoom';
import { format } from 'date-fns';

export { reportNativeFormValidity, unlockReadonlyInputs } from './formValidation';
export { applyExamShellBackground, isExamStageRoute } from './examShellBackground';
export { getSelfTestQrOrigin, buildSelfTestQrPayload } from './qrCodeOrigin';
export { getSelfTestPathFromQrPayload } from './selfTestQr';
export {
  formatHistoryArcMinute,
  formatHistoryScalar,
  hasRenderableTestResult,
  normalizeStoredTestResult,
  pickEyeResult,
} from './testResultEyes';
export { formatPatientDisplayName, sanitizeStoredPatientName } from './patientDisplayName';

export const handleDateFormat = (date: number, type: 'date' | 'time' | 'patient' | 'testHistoryTime'): string => {
  switch (type) {
    case 'date':
      return format(date, 'MMMM dd, yyyy');
    case 'time':
      return format(date, 'hh:mm:ss aa');
    case 'patient':
      return format(date, 'dd MMMM yyyy');
    case 'testHistoryTime':
      return format(date, 'hh:mm aa');
  }
};

// Tracks viewport size and refreshes after iOS orientation changes.
export const useWindowSize = (): Array<number> => {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }

    // iOS often fires orientationchange before innerWidth/innerHeight update.
    function updateSizeAfterOrientation() {
      requestAnimationFrame(() => {
        updateSize();
        window.setTimeout(updateSize, 250);
      });
    }

    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSizeAfterOrientation);
    window.visualViewport?.addEventListener('resize', updateSize);
    updateSize();

    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSizeAfterOrientation);
      window.visualViewport?.removeEventListener('resize', updateSize);
    };
  }, []);
  return size;
};

export {
  acceptCurrentViewportAsBaseline,
  installPatientViewportZoomGuards,
  isCalibrationRoute,
  isPatientZoomWarningRoute,
  isViewportZoomedFromBaseline,
  subscribeViewportZoomChanges,
} from './viewportZoom';

/** Subscribes to viewport resize and reports browser zoom vs the session baseline. */
export const useWindowZoom = (): boolean => {
  const [isZoomed, setIsZoomed] = useState(false);

  useLayoutEffect(() => {
    return subscribeViewportZoomChanges(() => {
      setIsZoomed(isViewportZoomedFromBaseline());
    });
  }, []);

  return isZoomed;
};

const buildDyopsSizesArray = (): Array<{ size: number }> => {
  const result = [];
  for (let i = 4; i < 104; i++) {
    if (i < 23 || i === 25) {
      i = i;
    } else if ((i > 25 && i < 28) || (i > 31 && i < 36)) {
      i += 2;
    } else if (i > 35 && i < 40) {
      i += 3;
    } else if ((i > 40 && i < 47) || (i > 51 && i < 63)) {
      i += 4;
    } else if (i > 46 && i < 63) {
      i += 5;
    } else if (i > 62 && i < 70) {
      i += 7;
    } else if (i > 70 && i < 81) {
      i += 10;
    } else if (i > 81 && i < 104) {
      i += 22;
    } else {
      i += 1;
    }
    result.push({ size: i });
  }
  return result;
};

export const dyopsSizesAlgorithm = (
  action: 'increase' | 'decrease',
  size: number,
  isIncreased: boolean,
  isFirstValue: boolean,
): { size: number } => {
  const dyopsSizesArray = buildDyopsSizesArray();

  switch (action) {
    case 'increase':
      if (size === 35 && isFirstValue) {
        return { size: 35 };
      } else {
        if (size !== 104) {
          const sizeIndex = dyopsSizesArray.findIndex((val) => val.size === size);
          return dyopsSizesArray[sizeIndex + 1] ? dyopsSizesArray[sizeIndex + 1] : { size: 35 };
        }
        return { size: 104 };
      }

    case 'decrease':
      if (size === 35 && isFirstValue) {
        return { size: 35 };
      }
      if (isIncreased) {
        const sizeIndex = dyopsSizesArray.findIndex((val) => val.size === size);
        return dyopsSizesArray[sizeIndex - 1] ? dyopsSizesArray[sizeIndex - 1] : { size: 4 };
      } else {
        if (size === 35) {
          return { size: 24 };
        }
        if (size === 24) {
          return { size: 17 };
        }
        if (size === 17) {
          return { size: 12 };
        }
        if (size === 12) {
          return { size: 8 };
        }
        if (size === 8 || size === 4) {
          return { size: 4 };
        }
        return { size: 4 };
      }
    default:
      return { size: 35 };
  }
};

export const getDyopsSize = (size: number, distance: string, unit: string): number => {
  const calculation = (size / 7.6) * (0.00225 * Number(unit === 'meter' ? distance : Number(distance) / 3.281) * 1000);
  const result = calculation < 11 ? Math.ceil(calculation / 0.25) * 0.25 : Math.round(calculation);
  return result;
};

const dyopsSnellenFeetDistance = (size: number): number => {
  const distanceFormula = 20 * Math.pow(10, Math.log10(size) * 1.765 - 1.557);

  if (size === 8 || size === 9 || size === 13 || size === 15 || size === 16 || size === 30 || size === 46) {
    return Math.floor(distanceFormula / 5) * 5;
  } else if (size === 5 || size === 6 || size === 11 || size === 12 || size === 14 || size === 22) {
    return Math.round(distanceFormula / 5) * 5;
  } else if (size === 52 || size === 57) {
    return Math.floor(distanceFormula / 50) * 50;
  } else if (size === 10 || size === 4) {
    return Math.floor(distanceFormula);
  } else if (size === 25) {
    return Math.ceil(distanceFormula / 10) * 10;
  } else if (size === 21) {
    return Math.floor(distanceFormula / 10) * 10;
  } else if (size === 19) {
    return Math.floor(distanceFormula / 90) * 90;
  } else if (size === 18) {
    return Math.floor(distanceFormula / 80) * 80;
  } else if (size === 17) {
    return Math.floor(distanceFormula / 75) * 75;
  } else if (size === 7) {
    return Math.ceil(distanceFormula);
  } else {
    return Math.round(distanceFormula / 50) * 50;
  }
};

const dyopsSnellenMeterDistance = (size: number): number => {
  const distanceFormula = 6.096 * Math.pow(10, Math.log10(size) * 1.765 - 1.557);

  if (size === 62 || size === 39 || size === 20 || size === 28) {
    return Math.floor(distanceFormula / 10) * 10;
  }
  if (size === 52) {
    return Math.floor(distanceFormula / 170) * 170;
  }
  if (size === 46 || size === 18 || size === 24 || size === 32) {
    return Math.floor(distanceFormula / 5) * 5;
  }
  if (size >= 57) {
    return Math.round(distanceFormula / 50) * 50;
  }
  if (size >= 35) {
    return Math.ceil(distanceFormula / 10) * 10;
  }
  if (size >= 4 && size <= 5) {
    return Math.ceil(distanceFormula / 2) * 2;
  }
  if (size === 6) {
    return Math.ceil(distanceFormula / 1.5) * 1.5;
  }
  if (size === 8 || size === 13) {
    return Math.floor(distanceFormula);
  }
  if (size === 9) {
    return Math.floor(distanceFormula / 1.5) * 1.5;
  }
  if (size === 10 || size === 15) {
    return Math.floor(distanceFormula / 0.5) * 0.5;
  }
  if (size === 16) {
    return Math.floor(distanceFormula / 3) * 3;
  }
  if (size === 17) {
    return Math.floor(distanceFormula / 23) * 23;
  }
  if (size === 19) {
    return Math.floor(distanceFormula / 27) * 27;
  }
  if (size === 21) {
    return Math.floor(distanceFormula / 34) * 34;
  }
  if (size === 30) {
    return Math.floor(distanceFormula / 67) * 67;
  } else {
    return Math.ceil(distanceFormula / 0.5) * 0.5;
  }
};

export const getDyopsDistance = (size: number, type: string): number => {
  switch (type) {
    case 'feet':
      return dyopsSnellenFeetDistance(size);
    case 'meter':
      return dyopsSnellenMeterDistance(size);
    case 'decimal':
      const result: number = 20 / dyopsSnellenFeetDistance(size);
      return Number(result.toFixed(3));
    default:
      return 0;
  }
};

export const dyopsRotationRandom = (): boolean => {
  const random = Math.floor(Math.random() * (3 - 1) + 1);

  const result = random !== 1 ? true : false;
  return result;
};

export const convertUnit = (unit: string, value: string): string => {
  switch (unit) {
    case 'meter':
      return String((Number(value) * 3.281).toFixed(2));
    case 'feet':
      return String((Number(value) / 3.281).toFixed(2));

    default:
      return value;
  }
};

export const handleConnectRoute = (inputType: string): string => {
  switch (inputType) {
    case 'doctorTest':
      return '/connect';
    case 'selfTest':
      return '/qr_connect';

    default:
      return '/connect';
  }
};
